import React from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { Form, Formik } from 'formik';
import lodashCompact from 'lodash/fp/compact';
import { useHistory } from 'react-router';

import {
  SR_API_ACTION_UTILITY_PARTNER_SIGNUP,
  SR_API_ACTION_UTILITY_USER_ACTIVATE_PARTNER_USER_ACCOUNT,
} from 'api/utilityActionTypes';

import {
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import {
  HUBSPOT_DEAL_ID_DIGITS_MESSAGE,
  HUBSPOT_DEAL_ID_REQUIRED_MESSAGE,
} from 'common/utility/constants';

import { initialApiActionState } from 'api/createApiClient';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import AuthActions from 'client/actions/UtilityAuth';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrButton, { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import UpSelfOnboardingHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingHeader';
import UpSelfOnboardingReferrerHeader from 'client/components/UPSelfOnboarding/UpSelfOnboardingReferrerHeader';

import './UpSelfOnboarding.less';

// Exported for tests: which fields page 1 renders is the whole behaviour of FEAT-C22, and
// the component itself can't be rendered outside React because of its hooks.
export const getFormFields = ({
  referrerId,
  emailToActivate,
  isReferrerLinkSignup,
  formValues,
}) => {
  const formFields = [
    hideFieldIf(emailToActivate, {
      id: 'name',
      title: 'Company Name',
      type: 'short_text',
      properties: {
        placeholder: 'type company name here...',
      },
      validations: ['required'],
    }),
    hideFieldIf(emailToActivate, {
      id: 'doorsUnderManagement',
      title: 'Doors Under Management',
      type: 'short_text',
      validations: ['required', 'nonNegativeNumber'],
      properties: {
        onlyAcceptNumbers: true,
        pattern: '[0-9]+',
        placeholder: 'type doors under management here...',
      },
    }),
    // Partners onboarding through an admin-generated referral link must be linked to their
    // HubSpot deal up front (FEAT-C22). Sales copies these `?referrerUserId=<sales-user-id>`
    // links out of the admin page and sends them on, so the partner filling this in is often
    // anonymous — the link itself is the marker, not the current session's roles. Open
    // self-serve signups and account activation never see this field.
    showFieldIf(isReferrerLinkSignup, {
      id: 'hubspotDealId',
      title: 'HubSpot Deal ID',
      subtitle:
        'Numbers only. Links this partner to their HubSpot deal — copy it from the deal URL in HubSpot.',
      type: 'short_text',
      properties: {
        // `onlyAcceptDigits` rather than `onlyAcceptNumbers`: the id is stored in a varchar
        // and must stay a string of digits, where `onlyAcceptNumbers` would parseFloat it
        // (dropping leading zeros and letting "1.5" / "1e5" through).
        onlyAcceptDigits: true,
        pattern: '[0-9]+',
        placeholder: 'type HubSpot deal id here...',
        className: 'selfOnboardingSubtitle',
      },
      validations: [
        `required::${HUBSPOT_DEAL_ID_REQUIRED_MESSAGE}`,
        `digits::${HUBSPOT_DEAL_ID_DIGITS_MESSAGE}`,
      ],
    }),
    {
      id: 'firstName',
      title: 'First Name',
      type: 'short_text',
      properties: {
        placeholder: 'type your first name...',
      },
      validations: ['required'],
    },
    {
      id: 'lastName',
      title: 'Last Name',
      type: 'short_text',
      properties: {
        placeholder: 'type your last name...',
      },
      validations: ['required'],
    },
    {
      id: 'phone',
      title: 'Phone',
      subtitle: 'Use your personal phone.',
      type: 'phone',
      properties: {
        placeholder: 'type your phone...',
        className: 'selfOnboardingSubtitle',
        dataDogMasked: false,
      },
      validations: ['required', 'phone'],
    },
    {
      id: 'email',
      title: 'Email',
      type: 'email',
      subtitle: 'Get the notifications for completed utilities.',
      properties: {
        placeholder: 'type your email...',
        className: lodashCompact([
          'selfOnboardingSubtitle',
          emailToActivate ? 'selfOnboardingDisabledInput' : null,
        ]).join(' '),
        dataDogMasked: false,
        disabled: emailToActivate,
      },
      validations: ['required', 'email'],
    },
    {
      id: 'password',
      title: 'New Password',
      type: 'password',
      properties: {
        placeholder: 'create new password...',
        showToggle: true,
      },
      validations: ['required'],
    },
    {
      id: 'confirmPassword',
      title: 'Confirm Password',
      type: 'password',
      properties: {
        placeholder: 'confirm password...',
        showToggle: true,
      },
      validations: ['required', 'confirmPassword:password'],
    },
    showFieldIf(!referrerId && !emailToActivate, {
      id: 'isReferral',
      title: 'Were you referred by someone?',
      type: 'yes_no',
      properties: {
        className: 'yesnoFullWidth',
      },
      validations: ['required'],
    }),
    showFieldIf(formValues?.isReferral, {
      id: 'referrerOtherName',
      title: 'Referrer Name',
      subtitle: 'Who referred you to Utility Profit?',
      type: 'short_text',
      properties: {
        placeholder: 'type referrer name here...',
        className: 'selfOnboardingSubtitle',
      },
      validations: ['required'],
    }),
    showFieldIf(!emailToActivate, {
      id: 'hasStudentHousingPortfolio',
      title: (
        <>
          Does student housing take up 30%+ <br />
          of your portfolio?
        </>
      ),
      type: 'yes_no',
      properties: {
        className: 'yesnoFullWidth',
      },
      validations: ['required'],
    }),
  ];
  return formFields;
};

/**
 * Whether page 1 is being completed for a partner that came in through an admin-generated
 * referral link, which is what makes the HubSpot Deal ID required (FEAT-C22).
 *
 * `referrerUserId` covers the first visit. Once the partner exists the rest of onboarding
 * runs from UtilityPartnerOverviewPage, which mounts a fresh UpSelfOnboarding without the
 * query param — so stepping back here from Brand would otherwise lose the field. A stored
 * `hubspotDealId` is the durable marker that this partner came in through the link.
 *
 * Exported for tests alongside `getFormFields`.
 */
export const isReferrerLinkSignup = ({ emailToActivate, referrerUserId, utilityPartner }) =>
  // Account activation reuses this step but never creates a partner, so it is excluded.
  Boolean(!emailToActivate && (referrerUserId || utilityPartner?.hubspotDealId));

const UpSelfOnboardingUserStep = ({
  referrer,
  activeIndex,
  referrerUserId,
  emailToActivate = null,
  utilityPartner,
  utilityPartnerGroup,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    signupApiState = initialApiActionState,
    activatePartnerUserAccountApiState = initialApiActionState,
  } = useBranch({
    utilityUser: ['utilityUser'],
    signupApiState: ['apiState', SR_API_ACTION_UTILITY_PARTNER_SIGNUP, 'default'],
    activatePartnerUserAccountApiState: [
      'apiState',
      SR_API_ACTION_UTILITY_USER_ACTIVATE_PARTNER_USER_ACCOUNT,
      'default',
    ],
  });

  const errorMessage =
    signupApiState.error?.message || activatePartnerUserAccountApiState.error?.message;
  const theme = utilityPartnerGroup?.defaultTheme || {};
  const onboardingOverrides = utilityPartnerGroup?.onboardingOverrides || {};

  // A partner created through the link already has its deal id stored, so reuse it both as
  // the gate (see `isReferrerLinkSignup`) and as the value to show when stepping back here.
  const partnerHubspotDealId = utilityPartner?.hubspotDealId || null;
  const isReferrerLink = isReferrerLinkSignup({
    emailToActivate,
    referrerUserId,
    utilityPartner,
  });

  const handlePartnerSignup = (formValues) => {
    return dispatch(AuthActions.partnerSignup, {
      ...formValues,
      referrerId: referrer?.id,
      referrerUtilityUserId: parseInt(referrerUserId, 10) || null,
      utilityPartnerGroupId: utilityPartnerGroup?.id,
      referrerOtherName: formValues?.isReferral ? formValues?.referrerOtherName : null,
      hasStudentHousingPortfolio: formValues?.hasStudentHousingPortfolio,
      // Hidden fields still carry an empty string, so normalize to null rather than
      // sending a blank id from the open self-serve flow.
      hubspotDealId: formValues?.hubspotDealId?.trim() || null,
    });
  };

  const handleActivatePartnerUserAccount = (formValues) => {
    return dispatch(AuthActions.activatePartnerUserAccount, {
      ...formValues,
      email: emailToActivate,
      onSuccess: () => {
        history.push('/utility-profit');
      },
    });
  };

  const onSubmit = (formValues) => {
    if (emailToActivate) {
      return handleActivatePartnerUserAccount(formValues);
    }
    return handlePartnerSignup(formValues);
  };

  const title = emailToActivate
    ? 'Activate your account'
    : onboardingOverrides?.userSectionTitle || 'Get utility setups automated!';

  const subtitle = emailToActivate
    ? 'Join your company’s Utility Profit account.'
    : 'It’s easy to set up, free, and tenants love it.';

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getFormFields({
          referrerId: referrer?.id,
          emailToActivate,
          isReferrerLinkSignup: isReferrerLink,
        }),
        defaultValues: {
          email: emailToActivate,
          hubspotDealId: partnerHubspotDealId,
        },
      })}
      validate={(values) =>
        createFormValidationFn(
          getFormFields({
            referrerId: referrer?.id,
            emailToActivate,
            isReferrerLinkSignup: isReferrerLink,
          }),
        )(values)
      }
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formFields = getFormFields({
          referrerId: referrer?.id,
          emailToActivate,
          isReferrerLinkSignup: isReferrerLink,
          formValues: formikBag.values,
        });

        return (
          <div styleName="layout">
            <div styleName="left">
              <UpSelfOnboardingHeader
                activeIndex={activeIndex}
                partnerGroupLogoUrl={utilityPartnerGroup?.logoUrl}
                partnerGroupPrimaryColor={theme.primaryColor}
                partnerGroupSecondaryColor={theme.secondaryColor}
                partnerGroupInactiveColor={theme.inactiveTextColor}
              />
              <div styleName="lineSeparator" />
              <Form styleName="form">
                {referrer && (
                  <UpSelfOnboardingReferrerHeader
                    referrerName={referrer?.name}
                    referrerDefaultAmount={referrer?.defaultReferralAmount}
                  />
                )}
                <span styleName="titleText">{title}</span>
                <span styleName="subTitleText">{subtitle}</span>
                {utilityPartnerGroup?.partnersCount > 0 && (
                  <span styleName="partnerGroupCountText">
                    {`📈 ${utilityPartnerGroup?.partnersCount} ${utilityPartnerGroup?.name}` +
                      ` ${pluralize(
                        'company',
                        utilityPartnerGroup?.partnersCount,
                      )} signed up recently.`}
                  </span>
                )}
                <SrBox margin="26px 0">
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      key={formField.id}
                      margin={index === 0 ? '0' : null}
                    />
                  ))}
                </SrBox>
                <SrBox margin="6px 0 10px">
                  {errorMessage && (
                    <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_RED}>
                      {errorMessage}
                    </SrErrorMessage>
                  )}
                </SrBox>
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  hasBoxShadow
                  type="submit"
                  themeOverrides={{
                    backgroundColor: theme?.buttonPrimaryColor,
                    color: theme?.buttonPrimaryTextColor,
                  }}
                >
                  Continue
                </SrButton>
              </Form>
            </div>
            <div styleName="right">
              <div styleName="content userStep fadeIn">
                {onboardingOverrides?.userSectionVideoUrl ? (
                  <div styleName="row">
                    <span styleName="titleText">
                      {onboardingOverrides?.userSectionVideoTitle || 'Quick onboarding video:'}
                    </span>
                    <div styleName="videoBox">
                      <iframe
                        title="video"
                        width="740px"
                        height="460px"
                        src={onboardingOverrides?.userSectionVideoUrl}
                        allow="accelerometer; autoplay; clipboard-write;"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <span styleName="titleText">Preview of Utility Profit:</span>
                    <div styleName="utilityActivationsTableBackgroundImg" />
                    <div styleName="utilityTestimonyBackgroundImg" />
                  </>
                )}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

UpSelfOnboardingUserStep.propTypes = {
  activeIndex: PropTypes.number,
  referrer: PropTypes.object,
  referrerUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  emailToActivate: PropTypes.string,
  utilityPartner: PropTypes.object,
  utilityPartnerGroup: PropTypes.object,
};

export default UpSelfOnboardingUserStep;
